<div class="review-offer-dialog">
  <div class="dialog-header">
    <div class="header-text">
      <h2 class="dialog-title">Review offer</h2>
      @if (isMulti && isPending) {
        <span class="plan-count-pill">{{ planCountLabel }}</span>
      }
    </div>
    <button
      mat-icon-button
      aria-label="Close"
      (click)="page === 3 ? done() : cancel()"
      [disabled]="isSubmitting"
    >
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Page 1: Details -->
  @if (page === 1) {
    <div class="dialog-body">
      <div class="dialog-content">
        @if (isPending) {
          <div class="stepper">
            <span class="step-dot active">1</span>
            <span class="step-label active">Details</span>
            <span class="step-line"></span>
            <span class="step-dot">2</span>
            <span class="step-label">Review</span>
            <span class="step-line"></span>
            <span class="step-dot">3</span>
            <span class="step-label">Confirmation</span>
          </div>
        }

        <div class="section-title">Details</div>
        <div class="info-row">
          <span class="label">Request ID</span>
          <span class="value tabular">{{
            data.transactionRequest.tx_request_id
          }}</span>
        </div>
        <div class="info-row">
          <span class="label">Supplier</span>
          <span class="value">{{
            data.transactionRequest.supplier_company
          }}</span>
        </div>
        <div class="info-row">
          <span class="label">Amount Due</span>
          <span class="value strong">{{ amountDueStr }}</span>
        </div>
        <div class="info-row">
          <span class="label">Status</span>
          <span class="value">{{
            data.transactionRequest.statusProductFormat
          }}</span>
        </div>
        <div class="info-row">
          <span class="label">Request Date</span>
          <span class="value">{{
            data.transactionRequest.created_at | date: "M/dd/yyyy"
          }}</span>
        </div>
        @if (data.transactionRequest.expires_at) {
          <div class="info-row">
            <span class="label">Expires At</span>
            <span class="value">{{
              data.transactionRequest.expires_at | date: "M/dd/yyyy"
            }}</span>
          </div>
        }

        <div class="memo-block">
          <div class="field-label">Memo</div>
          <div class="memo-box">{{ data.transactionRequest.memo || "—" }}</div>
        </div>

        @if (files.length > 0 && !loadingInvoices) {
          <div class="invoice-block">
            <div class="field-label">Invoice</div>
            @for (file of files; track file; let index = $index) {
              <a (click)="onOpenFile(index)">{{ file.name }}</a>
            }
          </div>
        } @else if (data.transactionRequest.invoice && !loadingInvoices) {
          <div class="invoice-block">
            <div class="field-label">Invoice</div>
            <span class="invoice-text">{{
              data.transactionRequest.invoice
            }}</span>
          </div>
        } @else if (loadingInvoices) {
          <div class="invoice-block">
            <mat-spinner [diameter]="20"></mat-spinner>
          </div>
        }

        <div class="href-links">
          @if (chosenPlanKey !== null) {
            <a (click)="onTermsClick()">Offer Summary &amp; Terms</a>
          } @else {
            <p>Click on a credit plan to view terms</p>
          }
        </div>

        @if (isPending) {
          <div class="plans-header">
            <span class="section-title">Choose a plan</span>
            @if (isMulti) {
              <span class="plans-hint">Pick the terms that work for you</span>
            }
          </div>
        } @else {
          <div class="plans-header">
            <span class="section-title">Plans offered</span>
          </div>
        }

        <div class="plan-list">
          @for (p of planCards; track p.key) {
            <div
              class="plan-card"
              [class.selected]="isPlanSelected(p.key)"
              [class.readonly]="!isPending"
              (click)="choosePlan(p.key)"
            >
              <div class="plan-card-top">
                <span class="radio" [class.on]="isPlanSelected(p.key)">
                  @if (isPlanSelected(p.key)) {
                    <span class="radio-dot"></span>
                  }
                </span>
                <div class="plan-meta">
                  <span class="plan-name">{{ p.label }}</span>
                  <div class="plan-desc">{{ p.desc }}</div>
                </div>
                <span class="plan-total">{{ p.totalStr }}</span>
              </div>
              <div class="plan-stats">
                <div>
                  <div class="stat-label">APR</div>
                  <div class="stat-value">{{ p.aprStr }}</div>
                </div>
                <div>
                  <div class="stat-label">Payment</div>
                  <div class="stat-value">{{ p.monthlyStr }}</div>
                </div>
                <div>
                  <div class="stat-label">Due</div>
                  <div class="stat-value">{{ p.dueStr }}</div>
                </div>
              </div>
              @if (p.hasFee) {
                <div class="fee-note">
                  Includes a {{ p.feeStr }} processing fee added by the
                  supplier.
                </div>
              }
            </div>
          }
        </div>
      </div>

      @if (isPending) {
        <div class="dialog-footer pending">
          @if (error) {
            <div class="error-message">{{ error }}</div>
          }
          @if (hasChosen) {
            <button
              type="button"
              class="taa-btn"
              [class.viewed]="taaViewedChosen"
              (click)="openTaa()"
              [disabled]="isSubmitting || loadingCreditCheck"
            >
              @if (taaViewedChosen) {
                <mat-icon>check</mat-icon>
              } @else {
                <mat-icon>description</mat-icon>
              }
              {{ taaBtnLabel }}
            </button>
          }
          @if (taaHint) {
            <div class="taa-hint">{{ taaHint }}</div>
          }
          <div class="action-buttons">
            <button
              mat-flat-button
              color="accent"
              class="primary-btn"
              (click)="goReviewPage()"
              [disabled]="!canReviewAccept || loadingCreditCheck"
            >
              @if (isSubmitting || loadingCreditCheck) {
                <mat-spinner [diameter]="20"></mat-spinner>
              } @else {
                Review &amp; Accept
              }
            </button>
            <button
              mat-stroked-button
              class="deny-btn"
              (click)="onDeclineClick()"
              [disabled]="isSubmitting || loadingCreditCheck"
            >
              Decline
            </button>
          </div>
        </div>
      } @else {
        <div class="dialog-footer decided">
          <span class="status-badge">{{
            data.transactionRequest.statusProductFormat
          }}</span>
          @if (authorizedLabel) {
            <span class="authorized-meta"
              >{{ authorizedLabel }} · {{ authorizedTotalStr }}</span
            >
          }
          <div class="spacer"></div>
          <button mat-stroked-button (click)="cancel()">Close</button>
        </div>
      }
    </div>
  }

  <!-- Page 2: Review & accept -->
  @if (page === 2) {
    <div class="overlay-page">
      <div class="overlay-header">
        <h2 class="dialog-title">Review &amp; accept</h2>
        <button
          mat-icon-button
          aria-label="Close"
          (click)="cancel()"
          [disabled]="isSubmitting"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <div class="overlay-body">
        <div class="stepper">
          <span class="step-dot done">1</span>
          <span class="step-label muted">Details</span>
          <span class="step-line"></span>
          <span class="step-dot active">2</span>
          <span class="step-label active">Review</span>
          <span class="step-line"></span>
          <span class="step-dot">3</span>
          <span class="step-label">Confirmation</span>
        </div>
        <p class="overlay-lead">
          Confirm the plan you selected from
          <strong>{{ data.transactionRequest.supplier_company }}</strong
          >. Accepting authorizes the terms you just viewed.
        </p>
        @if (chosenPlan; as plan) {
          <div class="summary-box">
            <div class="info-row">
              <span class="label">Plan</span
              ><span class="value strong">{{ plan.label }}</span>
            </div>
            <div class="info-row">
              <span class="label">Loan amount</span
              ><span class="value strong">{{ plan.totalStr }}</span>
            </div>
            <div class="info-row">
              <span class="label">APR</span
              ><span class="value">{{ plan.aprStr }}</span>
            </div>
            <div class="info-row">
              <span class="label">Term</span
              ><span class="value">{{ plan.termStr }}</span>
            </div>
            <div class="info-row">
              <span class="label">Payment</span
              ><span class="value">{{ plan.monthlyStr }}</span>
            </div>
            <div class="info-row">
              <span class="label">Due</span
              ><span class="value">{{ plan.dueStr }}</span>
            </div>
            <div class="info-row">
              <span class="label">Transaction ID</span>
              <span class="value tabular">{{
                data.transactionRequest.tx_request_id
              }}</span>
            </div>
          </div>
          @if (plan.hasFee) {
            <div class="info-callout">
              Includes a {{ plan.feeStr }} processing fee added by the supplier
              — disclosed in the Terms &amp; Agreement.
            </div>
          }
        }
        <div class="taa-viewed-bar">
          <mat-icon>check_circle</mat-icon>
          <span>Terms &amp; Agreement viewed</span>
          <button type="button" class="link-btn" (click)="openTaa()">
            View again
          </button>
        </div>
        <p class="finality-note">
          Once accepted, this plan is final and can no longer be changed or
          edited.
        </p>
        @if (acceptExceeds) {
          <div class="error-banner">{{ acceptExceedsMsg }}</div>
        }
        @if (acceptError) {
          <div class="error-banner">{{ acceptError }}</div>
        }
      </div>
      <div class="overlay-footer">
        <button
          mat-button
          type="button"
          (click)="backToDetails()"
          [disabled]="isSubmitting"
        >
          Back
        </button>
        @if (acceptExceeds) {
          <button mat-flat-button color="accent" class="flex-1" disabled>
            Insufficient credit
          </button>
        } @else {
          <button
            mat-flat-button
            color="accent"
            class="flex-1"
            (click)="onAcceptAuthorize()"
            [disabled]="!acceptEnabled || isSubmitting"
          >
            @if (isSubmitting) {
              <mat-spinner [diameter]="20"></mat-spinner>
            } @else {
              Accept &amp; authorize
            }
          </button>
        }
      </div>
    </div>
  }

  <!-- Page 3: Confirmation -->
  @if (page === 3) {
    <div class="overlay-page">
      <div class="overlay-header">
        <h2 class="dialog-title">Confirmation</h2>
        <button mat-icon-button aria-label="Close" (click)="done()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <div class="stepper padded">
        <span class="step-dot done">1</span>
        <span class="step-label muted">Details</span>
        <span class="step-line"></span>
        <span class="step-dot done">2</span>
        <span class="step-label muted">Review</span>
        <span class="step-line"></span>
        <span class="step-dot active">3</span>
        <span class="step-label active">Confirmation</span>
      </div>
      <div class="confirm-body">
        <div class="status-icon success"><mat-icon>check</mat-icon></div>
        <div class="status-title">Transaction authorized</div>
        <p class="status-body">
          You accepted <strong>{{ chosenPlan?.label }}</strong> for
          <strong>{{ chosenPlan?.totalStr }}</strong> from
          {{ data.transactionRequest.supplier_company }}. The plan is now locked
          and can no longer be edited. A signed copy of the agreement is in your
          Transactions.
        </p>
      </div>
      <div class="overlay-footer">
        <button mat-flat-button color="accent" class="flex-1" (click)="done()">
          Done
        </button>
      </div>
    </div>
  }
</div>
